/* eslint-disable no-shadow */
import { END, buffers, eventChannel } from "redux-saga";
import {
    call, cancel, cancelled, delay, flush, fork, race, take, takeEvery
} from "redux-saga/effects";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    ARCHIVE_REQUESTED,
    CREATE_REQUESTED, 
    DELETE_REQUESTED,
    LOAD_REQUESTED,
    REPLY_REQUESTED,
    SUBSCRIBE,
    UNSUBSCRIBE,
    UPDATE_CONTENT_REQUESTED,
    UPDATE_REQUESTED,
    UPSERT_REQUESTED,
    defaultScopeId,
    failCommentArchive,
    failCommentCreate,
    failCommentDelete,
    failCommentReply,
    failCommentUpdate,
    failCommentUpdatecontent,
    failCommentUpsert,
    failLoadComment,
    requestLoadComment,
    succeedCommentArchive,
    succeedCommentCreate,
    succeedCommentDelete,
    succeedCommentReply,
    succeedCommentUpdate,
    succeedCommentUpdatecontent,
    succeedCommentUpsert,
    successLoadComment,
    updateManyComments
} from "./comments.duck";

import {
    archiveComment,
    createComment,
    deleteComment,
    loadComment,
    replyToComment,
    subscribeComments,
    updateComment
} from "./comments.api";

import { getId, injectBoundActionCreatorsFactory } from "Utils/sagaUtils";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failCommentArchive,
        failCommentCreate,
        failCommentDelete,
        failCommentReply,
        failCommentUpdate,
        failCommentUpdatecontent,
        failCommentUpsert,
        failLoadComment,
        requestLoadComment,
        succeedCommentArchive,
        succeedCommentCreate,
        succeedCommentDelete,
        succeedCommentReply,
        succeedCommentUpdate,
        succeedCommentUpdatecontent,
        succeedCommentUpsert,
        successLoadComment,
        updateManyComments
    },
    getId,
    defaultScopeId
);

export function* createCommentSaga({
    data: { chunkId, content },
    actions: { succeedCommentCreate, failCommentCreate },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(createComment, content, { id: chunkId });
        yield succeedCommentCreate(result);
        yield call(resolve);
    } catch (e) {
        yield failCommentCreate(e);
        yield call(reject, e);
    }
}

export function* updateCommentSaga({
    data: { chunkId, ...data },
    actions: { succeedCommentUpdate, failCommentUpdate },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(updateComment, data, { id: chunkId });
        yield succeedCommentUpdate(result);
        yield call(resolve);
    } catch (e) {
        yield failCommentUpdate(e);
        yield call(reject, e);
    }
}

export function* replyCommentSaga({
    data: { chunkId, ...data },
    actions: { succeedCommentReply, failCommentReply },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(replyToComment, data, { id: chunkId });
        yield succeedCommentReply(result);
        yield call(resolve);
    } catch (e) {
        yield failCommentReply(e);
        yield call(reject, e);
    }
}

export function* archiveCommentSaga({
    data: { chunkId, commentId }, actions: { succeedCommentArchive, failCommentArchive }, resolve = noop, reject = noop 
}){
    try {
        const result  = yield call(archiveComment, commentId, { id: chunkId });
        yield succeedCommentArchive(result);
        yield call(resolve);
    } catch (e){
        yield failCommentArchive(e);
        yield call(reject, e);
    }
}

export function* deleteCommentSaga({ data, actions: { succeedCommentDelete, failCommentDelete }, resolve = noop, reject = noop }){
    try {
        const result  = yield call(deleteComment, data);
        yield succeedCommentDelete(result);
        yield call(resolve);
    } catch (e){
        yield failCommentDelete(e);
        yield call(reject, e);
    }
}

export function* updateContentCommentSaga({
    data: { chunkId, commentId, content }, actions: { succeedCommentUpdatecontent, failCommentUpdatecontent }, resolve = noop, reject = noop 
}){
    try {
        const result  = yield call(updateComment, commentId, content, { id: chunkId });
        yield succeedCommentUpdatecontent(result);
        yield call(resolve);
    } catch (e){
        yield failCommentUpdatecontent(e);
        yield call(reject, e);
    }
}

export function* loadCommentsSaga({ data, actions: { successLoadComment, failLoadComment }, resolve = noop, reject = noop }){
    try {
        const result  = yield call(loadComment, data);
        yield successLoadComment(result);
        yield call(resolve);
    } catch (e){
        yield failLoadComment(e);
        yield call(reject, e);
    }
}

export function* upsertCommentSaga({ data, actions: { succeedCommentUpsert, failCommentUpsert }, resolve = noop, reject = noop }){
    try {
        const apiFn = data.commentId ? updateComment : createComment;
        const result  = yield call(apiFn, data);
        yield succeedCommentUpsert(result);
        yield call(resolve);
    } catch (e){
        yield failCommentUpsert(e);
        yield call(reject, e);
    }
}

export const commentSubscription = subject => {
    return eventChannel(emitter => {
        try {
            return subscribeComments(subject, emitter);
        } catch (e){
            emitter(END);
        }
    },
    buffers.expanding(1) // buffer one invocation holding the most recent event
    );
};

export function* subscribeToComments({ subject, actions: { updateManyComments } }) {
    const chan = yield call(commentSubscription, subject);
    try {
        while (true) {
            const comments = yield flush(chan);
            if(comments.length) {
                yield updateManyComments({ comments: comments.flat() });
            }
            yield delay(ONE_SECOND);
        }
    } finally {
        if (yield cancelled()) {
            chan.close();
        }
    }
  
}

export function* subscribeCommentsSaga(){
    const subscriptions = {};
    const tasks = {};
    while ( true ) {
        const { subscribe, unsubscribe } = yield race({
            subscribe: take(SUBSCRIBE),
            unsubscribe: take(UNSUBSCRIBE)
        });
        const { data: { chunkId, commentId } } = subscribe || unsubscribe;
        const ids = [ chunkId, commentId && `id-${commentId}` ].filter(i => i);
        if(subscribe) {
            for(const subId of ids) {
                subscriptions[subId] = subscriptions[subId] || 0;
                subscriptions[subId]++;
                if(subscriptions[subId] === 1) {
                    tasks[subId] = yield fork(withBoundActions(subscribeToComments), { ...(subscribe || unsubscribe), subject: subId });
                }
            }
        }
        if(unsubscribe) {
            for(const subId of ids) {
                subscriptions[subId]--;
                if(subscriptions[subId] === 0) {
                    yield cancel(tasks[subId]);
                }
            
            }
        }
    }
}


export default () => function* rootSaga() {
    yield takeEvery(CREATE_REQUESTED, withBoundActions(createCommentSaga));
    yield takeEvery(UPDATE_REQUESTED, withBoundActions(updateCommentSaga));
    yield takeEvery(UPSERT_REQUESTED, withBoundActions(upsertCommentSaga));
    yield takeEvery(UPDATE_CONTENT_REQUESTED, withBoundActions(updateContentCommentSaga));
    yield takeEvery(DELETE_REQUESTED, withBoundActions(deleteCommentSaga));
    yield takeEvery(ARCHIVE_REQUESTED, withBoundActions(archiveCommentSaga));
    yield takeEvery(REPLY_REQUESTED, withBoundActions(replyCommentSaga));
    yield takeEvery(LOAD_REQUESTED, withBoundActions(loadCommentsSaga));
    yield fork(subscribeCommentsSaga);
};
